// Drillmaster Pell's lesson: a step-by-step guide on screen while you fight the training dummy. Each step ticks
// itself off when you do it (moved, waited for your bar, attacked, cast standing still, stepped out of a red
// circle, used an item), and the last one points at the glowing gate.
import type { BattleView, BattleEvent } from '../../../shared/battle.js';
import { barFor, abilityDef, type JobId } from '../../../shared/jobs.js';

interface Step { id: string; text: string; done: boolean }
const coarse = matchMedia('(pointer:coarse)').matches;

export class Tutorial {
  active = false;
  private steps: Step[] = [];
  private box: HTMLElement;
  private start: { x: number; y: number } | null = null;
  private dodging = new Set<number>();
  constructor() {
    this.box = document.createElement('div'); this.box.id = 'tutor'; this.box.className = 'tutor'; this.box.hidden = true;
    this.box.setAttribute('aria-live', 'polite'); document.body.append(this.box);
  }
  /** A battle update: is this the lesson, and did you just do one of the steps? */
  update(v: BattleView, events: BattleEvent[], me: number, job: JobId, pos: { x: number; y: number } | null): void {
    const lesson = v.fighters.some(f => f.side === 'foe' && f.mob === 'tutor_dummy');
    if (!lesson) { if (this.active) this.end(); return; }
    if (!this.active) this.begin(job, pos);
    const has = (id: string) => this.steps.some(s => s.id === id);
    if (pos && this.start && Math.hypot(pos.x - this.start.x, pos.y - this.start.y) > 20) this.tick('move');
    if (v.ready.includes(me)) this.tick('bar');
    for (const e of events) {
      if (e.k === 'hit' && e.from === me && !e.skill) this.tick('attack');
      if (e.k === 'use' && e.from === me && abilityDef(e.skill)?.kind === 'spell') this.tick('cast');
      if (e.k === 'use' && e.from === me && abilityDef(e.skill) && abilityDef(e.skill)!.kind !== 'spell' && has('skill')) this.tick('skill');
      if (e.k === 'telegraph') this.dodging.add(e.from);
      if (e.k === 'use' && this.dodging.has(e.from) && e.skill === 'flail') { this.dodging.delete(e.from); if (!e.to.includes(me)) this.tick('dodge'); }
      if (e.k === 'heal' && e.from === me && e.item) this.tick('item');
    }
    if (this.steps.filter(s => s.id !== 'gate').every(s => s.done)) this.tick('gate', false);
    this.render();
  }
  private begin(job: JobId, pos: { x: number; y: number } | null): void {
    this.active = true; this.start = pos; this.dodging.clear();
    const key = (k: string, t: string) => coarse ? t : k;
    const bar = barFor(job), spell = bar.map(id => abilityDef(id)).find(a => a?.kind === 'spell' && a.level <= 1), skill = bar.map(id => abilityDef(id)).find(a => a && a.kind !== 'spell' && a.level <= 1);
    this.steps = [
      { id: 'move', text: `Move around the ring (${key('WASD or the arrows', 'the thumb stick')}). You can move freely in a fight.`, done: false },
      { id: 'bar', text: 'Watch your time bar fill. When it glows, you can act.', done: false },
      { id: 'attack', text: `Attack (${key('J, or 1', 'the sword button')}): you dash in and strike.`, done: false },
      ...(spell ? [{ id: 'cast', text: `Cast ${spell.name} (${key('2', 'the bar')}): stand still while it gathers, or it breaks off.`, done: false }] : []),
      ...(!spell && skill ? [{ id: 'skill', text: `Use ${skill.name} from your bar (${key('2', 'tap it')}).`, done: false }] : []),
      { id: 'dodge', text: 'When a red circle appears on the floor, step out of it before it fills!', done: false },
      { id: 'item', text: `Drink a Potion (${key('F for items', 'Items')}). It heals you mid-fight.`, done: false },
      { id: 'gate', text: 'Well done! Finish the dummy, or walk into the glowing gate to leave.', done: false },
    ];
    this.box.hidden = false; this.render();
  }
  private tick(id: string, done = true): void { const s = this.steps.find(x => x.id === id); if (s && !s.done) { s.done = done; if (!done) (s as Step & { live?: boolean }).live = true; } }
  private render(): void {
    this.box.replaceChildren();
    const h = document.createElement('h4'); h.textContent = `🎯 Battle Basics · ${this.steps.filter(s => s.done).length} of ${this.steps.length}`; this.box.append(h);
    const cur = this.steps.find(s => !s.done && s.id !== 'gate') ?? this.steps[this.steps.length - 1];
    const ol = document.createElement('ol');
    for (const s of this.steps) { const li = document.createElement('li'); li.textContent = s.text; li.className = s.done ? 'done' : s === cur ? 'now' : ''; ol.append(li); }
    this.box.append(ol);
  }
  end(): void { this.active = false; this.box.hidden = true; this.steps = []; }
}
